import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Building2, Heart, Mail, ArrowRight, Home as HomeIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Seo from '@/components/Seo';
import Reveal from '@/components/Reveal';

const HERO_IMG = '/hero-chasnell.png';
const FOUNDER_IMG = '/founder-message.jpeg';

const relationshipOpportunities = [
  'Introductory conversations',
  'Family meetings',
  'Professional meetings',
  'Community meet-and-greets',
  'Small group outings',
  'Educational events',
  'Future home tours'
];

const pathways = [
  { title: 'About Forever Path Living™', description: 'Our story, philosophy, and the people we are being built to serve.', icon: <Users className="w-6 h-6" />, path: '/about' },
  { title: 'Interest & Referrals', description: 'Begin a conversation through our General Interest or Professional Referral form.', icon: <HomeIcon className="w-6 h-6" />, path: '/placement' },
  { title: 'Our Homes', description: 'How our organization intends to grow, thoughtfully, over time.', icon: <Building2 className="w-6 h-6" />, path: '/homes' },
  { title: 'The Chasnell Home™', description: 'Our first residential home in Richmond, Virginia.', icon: <Heart className="w-6 h-6" />, path: '/chasnell' },
  { title: 'Contact', description: 'Reach us by email or Instagram — we would love to hear from you.', icon: <Mail className="w-6 h-6" />, path: '/contact' }
];

const Home = () => {
  return (
    <div className="relative z-10 min-h-screen">
      <Seo title="Supportive Living in Virginia" description="Forever Path Living™ is a developing, relationship-centered supportive living organization in Virginia for adults with intellectual and developmental disabilities and co-occurring behavioral or mental health support needs. Virginia DBHDS initial licensure in progress — building relationships before opening our doors." />

      {/* Hero — founder-provided Chasnell Home image, shown clean & unobstructed */}
      <section className="bg-[#141810]" data-testid="home-hero">
        {/* Trust badge — centered beneath the header with generous spacing */}
        <div className="flex justify-center px-6 pt-8 sm:pt-10 pb-7 sm:pb-9">
          <div data-testid="hero-badge" className="flex items-center gap-2 pl-3 pr-4 py-1.5 rounded-full bg-forest-900/80 backdrop-blur-sm border border-gold-500/40 shadow-md max-w-[90%]">
            <span className="w-1.5 h-1.5 rounded-full bg-gold-500 flex-shrink-0" aria-hidden="true"></span>
            <p className="text-[11px] sm:text-xs md:text-sm text-cream-100 font-medium tracking-tight text-center">
              Virginia DBHDS Initial Licensure In Progress
            </p>
          </div>
        </div>

        {/* Hero image — the visual focus, no overlays */}
        <img
          src={HERO_IMG}
          alt="The Chasnell Home™ at dusk with warm lighting — Welcome Home. Building relationships before opening our doors. Because Every Story Deserves a Forever Path™."
          className="w-full max-h-[80vh] object-contain mx-auto"
        />

        {/* Primary CTA — below the image with its own breathing room */}
        <div className="flex justify-center px-6 pt-8 sm:pt-10 pb-10 sm:pb-14">
          <Link to="/placement">
            <Button data-testid="hero-cta-refer" className="rounded-full bg-gold-500 hover:bg-gold-700 text-forest-900 font-semibold px-8 py-6 text-base shadow-xl hover:-translate-y-0.5 transition-transform duration-300 group">
              Refer Someone
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 md:py-28 px-6 bg-cream-100">
        <Reveal className="max-w-3xl mx-auto text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sage-600 mb-5">A different kind of supportive living environment</p>
          <p className="text-lg md:text-xl text-forest-700 leading-relaxed mb-6">
            Forever Path Living™ was created from a real need to provide a home where individuals are supported as
            people first — not defined by diagnosis or past placements.
          </p>
          <div className="mt-10 space-y-2">
            <p className="font-serif text-3xl md:text-4xl text-forest-800">This is not just a placement.</p>
            <p className="font-serif text-3xl md:text-4xl text-gold-700 italic">This is a path forward.</p>
          </div>
        </Reveal>
      </section>

      {/* Building Relationships */}
      <section className="py-20 md:py-28 px-6 bg-sage-100" data-testid="relationships-section">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sage-600 mb-4">Building Relationships Before Opening Our Doors</p>
            <p className="text-lg md:text-xl text-forest-700 leading-relaxed max-w-3xl mx-auto">
              Forever Path Living™ believes relationships begin long before move-in day. As we complete the Virginia
              DBHDS licensing process, we hope to begin building meaningful relationships with prospective residents,
              families, guardians, case managers, support coordinators, and community partners.
            </p>
          </Reveal>

          <Reveal className="bg-cream-100 rounded-3xl p-8 md:p-12 border border-sage-200 shadow-[0_8px_30px_rgba(59,77,65,0.06)]">
            <p className="text-forest-800 font-medium mb-6">Future opportunities may include:</p>
            <div className="grid sm:grid-cols-2 gap-3.5">
              {relationshipOpportunities.map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-gold-500 flex-shrink-0" aria-hidden="true"></span>
                  <span className="text-forest-700">{item}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 pt-6 border-t border-sage-200">
              <p className="text-forest-700 leading-relaxed">
                Participation in these activities does not guarantee admission or placement. Their purpose is
                relationship-building, familiarity, trust, and informed decision-making.
              </p>
            </div>
            <div className="mt-8">
              <Link to="/placement">
                <Button data-testid="hero-begin-conversation-btn" className="rounded-full bg-forest-800 hover:bg-forest-900 text-cream-100 px-8 py-6 text-base font-semibold shadow-lg hover:-translate-y-0.5 transition-transform duration-300 group">
                  Begin a Conversation
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Founder's Message */}
      <section className="py-20 md:py-28 px-6 bg-cream-100">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-5xl text-forest-800">A Message from Our Founder</h2>
          </Reveal>
          <Reveal className="flex justify-center">
            <img
              src={FOUNDER_IMG}
              alt="A message from our founder, Shanté L. Fox"
              className="w-full max-w-4xl h-auto object-contain rounded-2xl border border-sage-200 shadow-[0_8px_30px_rgba(59,77,65,0.1)]"
              loading="lazy"
            />
          </Reveal>
        </div>
      </section>

      {/* Explore */}
      <section className="py-20 md:py-28 px-6 bg-cream-200">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-14">
            <h2 className="font-serif text-3xl md:text-5xl text-forest-800">Explore Forever Path Living™</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pathways.map((p, i) => (
              <Reveal key={p.path} delay={i * 70}>
                <Link to={p.path} className="group block h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-800 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-200 rounded-3xl" data-testid={`pathway-${p.path.slice(1) || 'home'}`}>
                  <Card className="h-full p-8 bg-cream-100 border border-sage-200 rounded-3xl transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(59,77,65,0.1)]">
                    <div className="inline-flex p-3.5 rounded-2xl bg-sage-100 text-forest-700 group-hover:bg-forest-800 group-hover:text-cream-100 transition-colors duration-300 mb-5">
                      {p.icon}
                    </div>
                    <h3 className="font-serif text-2xl text-forest-800 mb-3">{p.title}</h3>
                    <p className="text-forest-700 leading-relaxed mb-5">{p.description}</p>
                    <span className="inline-flex items-center text-gold-700 font-medium">
                      Learn more <ArrowRight className="ml-1.5 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Card>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
